import { shallowEqual } from "react-redux";
import { useState, useRef, useEffect } from "react";
import { useAppSelector, useAppDispatch } from "app/hooks";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

// Hooks
import useVcpNavActions from "./useVcpNavActions";

// Store
import { toggleLoader } from "store/slice/UI";
import { getInsightById, getProjectInsight } from "store/slice/Project/insights/insightsSlice";
import { fetchProjectCompany, getProjectCompanyData } from "store/slice/Project/projectData/ProjectSlice";
import { TNarrative, TPresetVcp, setPresetVcpId, setSelectedNarratives } from "store/slice/Vcp";

// Data
import { initialNarratives, narrativesData, preSetVcps } from "./VcpLayout.data";

// Utils
import { isEmpty } from "lodash";
import { useRedirectOnProjectError } from "utils";
import {
    getInitialFetchResponse,
    getInsightRelatedVcpData,
    TInitialFetch,
    TInitialFetchError,
} from "pages/ProjectDashboard/adapters/formattedData";

// Enums
import { urlPaths } from "enums/urlPaths";

// Types
import { NavButtonProps } from "components/HeaderNavigation/NavButton";
import { InsightData } from "services/projects/project.types";
import { flattenSectorProductsDomain } from "services/company";

const useVcpLayout = (): {
    company: ReturnType<typeof flattenSectorProductsDomain> | undefined;
    narratives: TNarrative[];
    hoveredItem: React.MutableRefObject<
        | {
              label: string;
              description: string;
          }
        | undefined
    >;
    error: string;
    selectedPreSettedVcp: React.MutableRefObject<TPresetVcp | undefined>;
    backButton: NavButtonProps;
    nextButton: NavButtonProps;
    updateHoveredPreSettedVcp: (
        item:
            | {
                  label: string;
                  description: string;
              }
            | undefined
    ) => void;
    isNarrativeSelected: (item: TNarrative) => boolean;
    handleSelectNarratives: (narrative: TNarrative) => void;
    handleSelectedVcp: (item: TPresetVcp) => void;
    handleSetNarrative: (narrative: Partial<TNarrative>) => void;
    setError: React.Dispatch<React.SetStateAction<string>>;
} => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const { companyId } = useParams();
    const [searchParams] = useSearchParams();
    const projectId = searchParams.get("projectId");
    const insightId = searchParams.get("insightId");
    const searchAiId = searchParams.get("searchAiId");

    // Redirect to 404 if invalid project id or not enough permission
    useRedirectOnProjectError([companyId, projectId, insightId]);

    // Company and Insight Data
    const { data: company } = useAppSelector(getProjectCompanyData);
    const { data: insightData } = useAppSelector(getInsightById(Number(insightId)), shallowEqual);

    // States
    const [error, setError] = useState("");
    const [narratives, setNarratives] = useState<TNarrative[]>(initialNarratives);

    // Flags
    const selectedPreSettedVcp = useRef<TPresetVcp>();
    const hoveredItem = useRef<{ label: string; description: string }>();

    const setNarrativeError = () => {
        setNarratives((currentNarratives) =>
            currentNarratives.map((narrative) => ({
                ...narrative,
                hasError: narrative.id.startsWith("narrative_") || narrative.weight === 0,
            }))
        );
    };

    const shouldContinue = () => {
        if (narratives.some((narrative) => narrative.id.startsWith("narrative_"))) {
            setNarrativeError();
            setError("setup-company.vcp.error.select.narrative.message");
            return true;
        }

        if (narratives.some((narrative) => narrative.weight === 0)) {
            setNarrativeError();
            setError("setup-company.vcp.error.select.weight.message");
            return true;
        }

        setNarratives((currentNarratives) => currentNarratives.map((narrative) => ({ ...narrative, hasError: false })));
        setError("");
        return false;
    };

    const { backButton, nextButton } = useVcpNavActions(
        companyId,
        projectId,
        insightId,
        searchAiId,
        selectedPreSettedVcp,
        narratives,
        shouldContinue
    );

    const updateHoveredPreSettedVcp = (item: typeof hoveredItem.current) => {
        hoveredItem.current = item;
    };

    const isNarrativeSelected = (item: TNarrative) => {
        return Boolean(narratives.find((selected) => item.id === selected.id));
    };

    const handleSelectNarratives = (narrative: TNarrative) => {
        const exists = narratives.find((e) => e.id === narrative.id);
        selectedPreSettedVcp.current = undefined;

        if (exists) {
            setNarratives((currentNarratives) =>
                currentNarratives.map((item, i) =>
                    narrative.id === item.id ? { id: `narrative_${i++}`, label: `Narrative ${i++}`, weight: 0 } : item
                )
            );
            return;
        }

        const firstEmptySpot = narratives.find((e) => e.id.includes("narrative_"));
        const indexOfNextEmptySpot = firstEmptySpot && narratives.indexOf(firstEmptySpot);

        if (typeof indexOfNextEmptySpot === "number") {
            setNarratives((currentNarratives) =>
                currentNarratives.map((item, i) => (indexOfNextEmptySpot === i ? narrative : item))
            );
        }
    };

    const handleSelectedVcp = (item: TPresetVcp) => {
        if (selectedPreSettedVcp.current?.id !== item.id) {
            selectedPreSettedVcp.current = item;
            setNarratives(
                selectedPreSettedVcp.current?.narratives?.map((e) => {
                    const narrativeFromData = narrativesData.find((data) => data.id === e.id) as TNarrative;
                    return {
                        ...narrativeFromData,
                        ...e,
                    };
                })
            );
        } else {
            selectedPreSettedVcp.current = undefined;
            setNarratives(initialNarratives);
        }
    };

    const handleSetNarrative = (narrative: Partial<TNarrative>) => {
        setNarratives((currentValues) =>
            currentValues.map((current) =>
                current.id === narrative?.id ? { ...current, weight: narrative?.weight || 0 } : current
            )
        );
    };

    // Update the store with the selected data
    useEffect(() => {
        dispatch(setPresetVcpId(selectedPreSettedVcp.current?.id));
        dispatch(setSelectedNarratives(narratives));
    }, [dispatch, narratives]);

    // If editing, fetch project data
    useEffect(() => {
        const hasCompanyData = !isEmpty(company);
        const hasInsightData = insightData;

        const initialFetch = async () => {
            let result = {} as TInitialFetch | TInitialFetchError;
            const promises = [];

            dispatch(toggleLoader(true));

            if (!hasCompanyData && companyId) {
                promises.push(dispatch(fetchProjectCompany(companyId)));
            }

            if (!hasInsightData && projectId && insightId) {
                promises.push(
                    dispatch(
                        getProjectInsight({
                            projectId,
                            path: `/project_${projectId}/insight_list/insight_${insightId}`,
                        })
                    )
                );
            }

            if (promises.length) {
                const response = await Promise.allSettled(promises);
                result = getInitialFetchResponse(response);

                if ("error" in result) {
                    dispatch(toggleLoader(false));
                    console.error(result.reason);
                    navigate(urlPaths.NotFound);
                    return;
                }
            }

            const insightParam = "insight" in result ? result.insight : insightData;
            await setData(insightParam);

            dispatch(toggleLoader(false));
        };

        const setData = (insightData: InsightData | undefined) => {
            if (insightData === undefined) return;

            const vpcData = getInsightRelatedVcpData(insightData);

            if (vpcData?.data.narratives?.length) {
                selectedPreSettedVcp.current = preSetVcps.find((item) => item.id === vpcData.data.preset);
                const selectedNarratives = vpcData.data.narratives.map((narrative) => {
                    const extraData = narrativesData.find((n) => n.id.toUpperCase() === narrative.type) as TNarrative;
                    return {
                        ...extraData,
                        weight: narrative.weight,
                    };
                });

                setNarratives(selectedNarratives);
            }
        };

        initialFetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        company,
        narratives,
        hoveredItem,
        error,
        selectedPreSettedVcp,
        backButton,
        nextButton,
        updateHoveredPreSettedVcp,
        isNarrativeSelected,
        handleSelectNarratives,
        handleSelectedVcp,
        handleSetNarrative,
        setError,
    };
};

export default useVcpLayout;
